/**
 * One `web.*` view, rendered from its own rows.
 *
 * THERE IS NO PER-VIEW CODE HERE AND THERE MUST NOT BE. The columns come from the keys
 * the rows actually carry, so migration 1.7 adding `web.player_warp_uses` - or adding a
 * column to an existing view - shows up in the inspector with no change to the SPA. That
 * is the same property the schema-driven editor is being built for, applied to read mode:
 * the day someone has to come back here to teach the table about a new field is the day
 * the design stopped holding.
 *
 * The three leading columns (`player_uuid`, `server_id`, `last_known_name`) are hidden by
 * default because the page already identifies the player; everything else is shown.
 */
import type { WebViewName } from '@dimensionscore/shared';

import type { PolledView } from '../api/usePolledView';

/** Present on every row of every view; the page header already says who this is. */
const PLAYER_COLUMNS = ['player_uuid', 'server_id', 'last_known_name'];

export interface ViewCardProps {
  readonly view: WebViewName;
  readonly title: string;
  /** One line explaining what the view is for, since column names are terse. */
  readonly caption?: string;
  /**
   * Any view's state. `object` rather than a specific row type because this component is
   * deliberately blind to which view it is drawing - a `PolledView<PlayerHomeRow>` and a
   * `PolledView<PlayerBaseTrustRow>` are equally welcome.
   */
  readonly state: PolledView<object>;
  /** Wording for "there is nothing here", which is usually a fact and not a failure. */
  readonly emptyText?: string;
}

export function ViewCard({ view, title, caption, state, emptyText }: ViewCardProps): React.JSX.Element {
  // The one cast in the component, and the reason it is safe: every `web.*` row is a flat
  // JSON object off the wire, whatever TypeScript interface names its columns upstream.
  const rows = (state.rows ?? []) as readonly Record<string, unknown>[];
  const columns = columnsOf(rows);

  return (
    <section className="dc-card dc-view" data-testid={`view-${view}`} data-view={view}>
      <header className="dc-view-head">
        <h2>{title}</h2>
        <span className="dc-view-count" data-testid={`view-count-${view}`}>
          {state.status === 'loading' && state.rows === null ? '...' : rows.length}
        </span>
        <Freshness state={state} />
      </header>
      {caption !== undefined && <p className="dc-dim dc-view-caption">{caption}</p>}

      {state.status === 'error' ? (
        <p className="dc-error" role="status" data-testid={`view-error-${view}`}>
          {state.error}
        </p>
      ) : rows.length === 0 && state.status === 'ready' ? (
        <p className="dc-dim" data-testid={`view-empty-${view}`}>
          {emptyText ?? 'Nothing recorded.'}
        </p>
      ) : (
        <div className="dc-table-scroll">
          <table className="dc-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column} scope="col">
                    {humanise(column)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={rowKey(row, index)} data-testid="view-row">
                  {columns.map((column) => (
                    <td key={column} data-column={column}>
                      <Cell value={row[column]} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Freshness({ state }: { state: PolledView<object> }): React.JSX.Element | null {
  if (state.checkedAt === null) return null;
  // notModifiedCount is surfaced because it is the cheap answer, and seeing it climb is
  // how an operator knows the conditional-GET path is actually working.
  const title = `${state.fetchCount} full ${state.fetchCount === 1 ? 'read' : 'reads'}, ${state.notModifiedCount} unchanged`;
  return (
    <time className="dc-view-fresh" dateTime={new Date(state.checkedAt).toISOString()} title={title} data-testid="view-freshness">
      {new Date(state.checkedAt).toLocaleTimeString()}
    </time>
  );
}

/**
 * Column order is first-seen order across the rows, which is PostgREST's column order and
 * therefore the view's own. Union rather than "keys of row 0" so a nullable column that
 * happens to be absent from the first row is not dropped.
 */
function columnsOf(rows: readonly Record<string, unknown>[]): string[] {
  const seen: string[] = [];
  for (const row of rows) {
    for (const key of Object.keys(row)) {
      if (!PLAYER_COLUMNS.includes(key) && !seen.includes(key)) seen.push(key);
    }
  }
  return seen;
}

function rowKey(row: Record<string, unknown>, index: number): string {
  const id = row['id'];
  return typeof id === 'number' || typeof id === 'string' ? String(id) : `row-${index}`;
}

/** `base_owner_name` -> `Base owner name`. Terse SQL names, readable headings. */
export function humanise(column: string): string {
  const words = column.replace(/_/g, ' ').trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

const ISO_INSTANT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/;

function Cell({ value }: { value: unknown }): React.JSX.Element {
  if (value === null || value === undefined) return <span className="dc-null">-</span>;
  if (typeof value === 'boolean') return <span className={value ? 'dc-yes' : 'dc-no'}>{value ? 'yes' : 'no'}</span>;
  if (typeof value === 'number') return <span className="dc-num">{value}</span>;
  if (typeof value === 'string') {
    // PostgREST renders timestamptz as ISO-8601; showing the local rendering is what an
    // admin wants, and the machine-readable original stays in the title.
    if (ISO_INSTANT.test(value)) {
      return (
        <time dateTime={value} title={value}>
          {new Date(value).toLocaleString()}
        </time>
      );
    }
    return <>{value}</>;
  }
  // Objects are `display_item` (an ItemSpec document) and `payload` (the RewardPayload
  // union). Neither has a bespoke renderer yet - R5 gives them one - and showing the JSON
  // is honest in the meantime.
  return <pre className="dc-json">{JSON.stringify(value, null, 1)}</pre>;
}
